import { motion } from 'framer-motion';
import {
  Target,
  ClipboardCheck,
  BarChart3,
  TrendingUp,
  Shield,
  ArrowRight,
  Phone,
  CheckCircle2,
  Building,
  Rocket,
  Landmark,
  Users
} from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

const scopeServices = [
  {
    icon: ClipboardCheck,
    title: 'Strategic Diagnostic & SWOT Mendalam',
    description: 'Analisis komprehensif kondisi internal dan eksternal bisnis Anda'
  },
  {
    icon: BarChart3,
    title: 'Analisis PESTEL & Competitive Benchmarking',
    description: 'Pemetaan faktor makro dan posisi kompetitif di industri'
  },
  {
    icon: Target,
    title: 'Review dan Validasi Strategic Objectives',
    description: 'Penyelarasan tujuan strategis dengan kondisi realitas bisnis'
  },
  {
    icon: TrendingUp,
    title: 'Rancang Roadmap Implementasi & KPI',
    description: 'Pembuatan peta jalan dan indikator kinerja yang terukur'
  },
  {
    icon: Shield,
    title: 'Audit Proses Pengambilan Keputusan & Governance',
    description: 'Evaluasi efektivitas sistem tata kelola dan pengambilan keputusan'
  },
  {
    icon: Users,
    title: 'Rekomendasi Perbaikan Sistem Monitoring & Control',
    description: 'Saran pengembangan sistem pengawasan dan kontrol operasional'
  }
];

const suitableFor = [
  {
    icon: Building,
    title: 'Bisnis Skala Menengah - Besar & BUMN',
    description: 'Yang memerlukan strategic refresh atau turnaround plan'
  },
  {
    icon: TrendingUp,
    title: 'Bisnis yang Siap Scale-Up',
    description: 'Dengan panduan strategi yang terukur dan jelas'
  },
  {
    icon: Rocket,
    title: 'Startup & Perusahaan Teknologi',
    description: 'Yang ingin fundable dengan roadmap pertumbuhan jelas'
  },
  {
    icon: Landmark,
    title: 'Organisasi Non-Profit & Institusi Pemerintah',
    description: 'Untuk efisiensi kebijakan dan program'
  }
];

const mainServices = [
  {
    icon: ClipboardCheck,
    number: '01',
    title: 'Strategic Audit & Health Check',
    features: [
      'Diagnostic workshop dan kuesioner manajemen',
      'Penilaian maturity level strategi dan governance'
    ]
  },
  {
    icon: Target,
    number: '02',
    title: 'Strategy Formulation & Alignment',
    features: [
      'Facilitated strategy-setting session (vision, mission, values)',
      'Penyusunan Balanced Scorecard & OKR',
      'Alignment sesi dengan tim operasional dan eksekutif'
    ]
  },
  {
    icon: TrendingUp,
    number: '03',
    title: 'Implementation Roadmap & KPI Design',
    features: [
      'Perancangan workstream & timeline implementasi',
      'Definisi KPI, milestone, dan ownership tiap inisiatif',
      'Tools dashboard monitoring dan template laporan'
    ]
  },
  {
    icon: Shield,
    number: '04',
    title: 'Continuous Monitoring & Strategy Audit',
    features: [
      'Quarterly strategy review dan healthcheck',
      'Audit adherence proses governance & decision-making',
      'Rekomendasi corrective action dan update strategi'
    ]
  }
];

export function BusinessStrategyAudit() {
  const { ref: heroRef, isInView: heroInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: scopeRef, isInView: scopeInView } = useScrollAnimation({ threshold: 0.1 });
  const { ref: servicesRef, isInView: servicesInView } = useScrollAnimation({ threshold: 0.1 });

  return (
    <div className="min-h-screen bg-dark-900">
      {/* Hero Section */}
      <section
        ref={heroRef}
        className="relative pt-32 pb-20 overflow-hidden"
      >
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/business-strategy-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="relative max-w-7xl mx-auto section-padding z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <div className="flex items-center gap-4 mb-4">
              <span className="text-6xl font-display font-bold text-white/10">07</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-6">
              Business Strategy Development & Audit
            </h1>

            <p className="text-xl text-white/70 mb-8 leading-relaxed">
              Kami membantu <strong className="text-primary-400">merancang dan memeriksa strategi bisnis Anda secara menyeluruh</strong>—dari analisis internal-eksternal hingga roadmap implementasi—agar setiap langkah pertumbuhan terukur dan terhindar dari risiko tersembunyi.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Scope & Suitable For Section */}
      <section ref={scopeRef} className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto section-padding">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={scopeInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Scope Layanan
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              Layanan ini mencakup seluruh aspek pengembangan dan audit strategi bisnis
            </p>
          </motion.div>

          {/* Scope Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {scopeServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={scopeInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
                className="glass rounded-2xl p-6 hover:border-primary-500/30 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center mb-4">
                  <service.icon className="w-6 h-6 text-primary-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                <p className="text-white/60 text-sm">{service.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Suitable For Section */}
          <div className="glass rounded-2xl p-8 md:p-12">
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-white mb-6 text-center">
              Cocok Untuk
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {suitableFor.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex items-start gap-4 p-4 bg-white/5 rounded-xl"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">{item.title}</h4>
                    <p className="text-white/60 text-sm">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Main Services Section */}
      <section ref={servicesRef} className="py-20 relative overflow-hidden">
        {/* Video Background - z-0 to stay at back */}
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover object-center z-0"
        >
          <source src="/business-strategy-services-bg.mp4" type="video/mp4" />
        </video>
        
        {/* Dark Navy Blue Overlay */}
        <div className="absolute inset-0 bg-[#0a1628]/85 z-[1]" />

        <div className="max-w-7xl mx-auto section-padding relative z-[2]">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              Layanan Utama
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {mainServices.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass rounded-2xl p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center flex-shrink-0">
                    <service.icon className="w-6 h-6 text-primary-400" />
                  </div>
                  <div className="flex-1">
                    <span className="text-2xl font-bold text-primary-400 mb-2 block">{service.number}</span>
                    <h3 className="text-xl font-semibold text-white mb-4">{service.title}</h3>
                    
                    <div className="space-y-2">
                      {service.features.map((feature) => (
                        <div key={feature} className="flex items-start gap-2">
                          <CheckCircle2 className="w-4 h-4 text-primary-400 flex-shrink-0 mt-0.5" />
                          <span className="text-white/70 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-dark-800/50">
        <div className="max-w-7xl mx-auto section-padding">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="glass rounded-2xl p-8 md:p-12 text-center"
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary-600/20 flex items-center justify-center">
                <Target className="w-6 h-6 text-primary-400" />
              </div>
            </div>

            <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-4">
              Siap Mengembangkan Strategi Bisnis?
            </h2>

            <p className="text-white/60 mb-8 max-w-2xl mx-auto">
              Dapatkan audit dan pengembangan strategi bisnis yang komprehensif untuk pertumbuhan yang terukur.
            </p>

            <motion.a
              href={`tel:${companyInfo.phone}`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="inline-flex items-center gap-2 px-8 py-4 bg-primary-600 text-white font-semibold rounded-full hover:bg-primary-700 transition-colors shadow-lg shadow-primary-500/30"
            >
              <Phone className="w-5 h-5" />
              Konsultasi Gratis
              <ArrowRight className="w-5 h-5" />
            </motion.a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
